import React, { useRef, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import RestaurantCard from "./RestaurantCard";
import AddRestaurantCTA from "./AddRestaurantCTA";
import EmptyState from "./EmptyState";
import "./CardCarousel.css";

const CardCarousel = ({
  restaurants,
  currentLocation,
  selectedRestaurantId,
  onRestaurantSelect,
  onScrollEnd,
  onExpandCard,
  onAdHocSearch,
  searchQuery,
}) => {
  const carouselRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const isProgrammaticScrollRef = useRef(false);
  const isInitialMountRef = useRef(true);

  // Get the actual card width and gap from the DOM
  const getCardMetrics = useCallback(() => {
    if (!carouselRef.current) return { cardWidth: 0, gap: 12 };
    const firstCard = carouselRef.current.querySelector('.card-carousel-item');
    if (!firstCard) return { cardWidth: 0, gap: 12 };
    const cardWidth = firstCard.offsetWidth;
    // Gap is --spacing-md = 12px
    return { cardWidth, gap: 12 };
  }, []);

  // Scroll to a specific index
  const scrollToIndex = useCallback((index, behavior = "smooth") => {
    if (!carouselRef.current || index < 0 || index >= restaurants.length) return;

    const { cardWidth, gap } = getCardMetrics();
    if (cardWidth === 0) return;

    const scrollLeft = index * (cardWidth + gap);

    // Mark as programmatic scroll to ignore scroll events
    isProgrammaticScrollRef.current = true;

    carouselRef.current.scrollTo({
      left: scrollLeft,
      behavior: behavior,
    });

    // Reset after scroll animation completes (or immediately for instant)
    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, behavior === "smooth" ? 500 : 50);
  }, [restaurants.length, getCardMetrics]);

  // Handle external selection changes (from map pin clicks)
  useEffect(function scrollToSelectedRestaurant() {
    if (!selectedRestaurantId || restaurants.length === 0) return;

    // Skip if this was triggered by our own scroll/click action
    if (isProgrammaticScrollRef.current) return;

    const index = restaurants.findIndex((r) => r._id === selectedRestaurantId);
    if (index >= 0) {
      // Use instant scroll on initial mount (returning from expanded state)
      // Use smooth scroll for subsequent changes (map pin clicks)
      const behavior = isInitialMountRef.current ? "instant" : "smooth";
      scrollToIndex(index, behavior);
      isInitialMountRef.current = false;
    }
  }, [selectedRestaurantId, restaurants, scrollToIndex]);

  // Handle scroll end - detect which card is now centered (user swipe only)
  const handleScroll = useCallback(() => {
    // Ignore programmatic scrolls
    if (isProgrammaticScrollRef.current) return;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (!carouselRef.current || isProgrammaticScrollRef.current) return;

      const { cardWidth, gap } = getCardMetrics();
      if (cardWidth === 0) return;

      const scrollLeft = carouselRef.current.scrollLeft;
      const visibleIndex = Math.round(scrollLeft / (cardWidth + gap));

      if (visibleIndex >= 0 && visibleIndex < restaurants.length) {
        const restaurant = restaurants[visibleIndex];

        // Update selection if it changed
        if (restaurant._id !== selectedRestaurantId) {
          // Mark as programmatic to prevent useEffect from scrolling
          isProgrammaticScrollRef.current = true;

          if (onScrollEnd) {
            onScrollEnd(restaurant);
          }
          if (onRestaurantSelect) {
            onRestaurantSelect(restaurant._id, restaurant);
          }

          // Reset after a delay
          setTimeout(() => {
            isProgrammaticScrollRef.current = false;
          }, 100);
        }
      }
    }, 150);
  }, [restaurants, selectedRestaurantId, onScrollEnd, onRestaurantSelect, getCardMetrics]);

  // Handle card click
  const handleCardClick = useCallback(
    (restaurant, index) => {
      // If this card is already selected, expand it
      if (restaurant._id === selectedRestaurantId) {
        if (onExpandCard) {
          onExpandCard(restaurant);
        }
      } else {
        // Different card - scroll to it and select it
        isProgrammaticScrollRef.current = true;
        scrollToIndex(index);
        if (onRestaurantSelect) {
          onRestaurantSelect(restaurant._id, restaurant);
        }
      }
    },
    [selectedRestaurantId, scrollToIndex, onRestaurantSelect, onExpandCard]
  );

  // Cleanup
  useEffect(function cleanupScrollTimeout() {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  if (!restaurants || restaurants.length === 0) {
    return <EmptyState onAdHocSearch={onAdHocSearch} />;
  }

  return (
    <div className="card-carousel-wrapper">
      <div
        ref={carouselRef}
        className="card-carousel"
        onScroll={handleScroll}
      >
        {restaurants.map((restaurant, index) => (
          <div
            key={restaurant._id}
            className="card-carousel-item"
          >
            <RestaurantCard
              restaurant={restaurant}
              currentLocation={currentLocation}
              isSelected={restaurant._id === selectedRestaurantId}
              onClick={() => handleCardClick(restaurant, index)}
              searchQuery={searchQuery}
            />
          </div>
        ))}
        {/* CTA card at the end */}
        <div className="card-carousel-item card-carousel-cta">
          <AddRestaurantCTA variant="card" onAdHocSearch={onAdHocSearch} />
        </div>
      </div>
    </div>
  );
};

CardCarousel.propTypes = {
  restaurants: PropTypes.array.isRequired,
  currentLocation: PropTypes.object.isRequired,
  selectedRestaurantId: PropTypes.string,
  onRestaurantSelect: PropTypes.func,
  onScrollEnd: PropTypes.func,
  onExpandCard: PropTypes.func,
  onAdHocSearch: PropTypes.func,
};

export default CardCarousel;
